import { Component, type ErrorInfo, type ReactNode } from 'react';

import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import i18n from '@/i18n/config';

interface Props {
  children: ReactNode;
  portal?: string;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', this.props.portal ?? 'app', error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  private reload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">
            {this.props.portal
              ? i18n.t('hire.errorBoundary.titleWithPortal', { portal: this.props.portal })
              : i18n.t('hire.errorBoundary.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {i18n.t('hire.errorBoundary.description')}
          </p>
          {import.meta.env.DEV && (
            <pre className="text-start text-xs text-muted-foreground bg-muted rounded-md p-3 overflow-auto max-h-40">
              {error.message}
            </pre>
          )}
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={this.reset}>{i18n.t('hire.errorBoundary.tryAgain')}</Button>
            <Button onClick={this.reload}>{i18n.t('hire.errorBoundary.reload')}</Button>
          </div>
        </div>
      </div>
    );
  }
}
